import {
  OG_IMAGE,
  SITE_DESCRIPTION,
  SITE_KEYWORDS,
  SITE_NAME,
  SITE_ORIGIN,
  SITE_TITLE,
  absoluteUrl,
} from '@/config/site'

export interface SeoOptions {
  title?: string
  description?: string
  keywords?: string
  /** Hash 路由路径，如 `/` 或 `/settings` */
  path?: string
  noindex?: boolean
}

const STRUCTURED_DATA_ID = 'fpstrain-jsonld'

function upsertMeta(
  attr: 'name' | 'property',
  key: string,
  content: string,
): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    document.head.appendChild(el)
  }
  el.href = href
}

export function applySeo(options: SeoOptions = {}): void {
  const title = options.title ?? SITE_TITLE
  const description = options.description ?? SITE_DESCRIPTION
  const keywords = options.keywords ?? SITE_KEYWORDS
  const canonical = absoluteUrl(options.path ?? '/')

  document.title = title

  upsertMeta('name', 'description', description)
  upsertMeta('name', 'keywords', keywords)
  upsertMeta('name', 'robots', options.noindex ? 'noindex, nofollow' : 'index, follow')

  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', SITE_NAME)
  upsertMeta('property', 'og:title', title)
  upsertMeta('property', 'og:description', description)
  upsertMeta('property', 'og:url', canonical)
  upsertMeta('property', 'og:image', OG_IMAGE)
  upsertMeta('property', 'og:locale', 'zh_CN')

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', title)
  upsertMeta('name', 'twitter:description', description)
  upsertMeta('name', 'twitter:image', OG_IMAGE)

  upsertLink('canonical', canonical)
}

export function injectStructuredData(): void {
  const existing = document.getElementById(STRUCTURED_DATA_ID)
  if (existing) return

  const payload = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${SITE_ORIGIN}/#website`,
        url: SITE_ORIGIN,
        name: SITE_NAME,
        description: SITE_DESCRIPTION,
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'WebApplication',
        '@id': `${SITE_ORIGIN}/#app`,
        name: SITE_NAME,
        url: SITE_ORIGIN,
        applicationCategory: 'GameApplication',
        operatingSystem: 'Web Browser',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'CNY' },
        description: SITE_DESCRIPTION,
        browserRequirements: 'Requires JavaScript. Chrome or Edge recommended.',
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'FpsTrain 是什么？需要下载吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FpsTrain 是浏览器内的 FPS 枪法训练器，打开网页即可练习，无需安装客户端。',
            },
          },
          {
            '@type': 'Question',
            name: '适合练哪些游戏？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Gridshot 模式侧重甩枪与多目标切换，适合 Valorant、CS2、Apex 等 FPS 开黑前热身。',
            },
          },
          {
            '@type': 'Question',
            name: '用什么浏览器体验最好？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '建议使用最新版 Chrome 或 Edge，以获得更稳定的鼠标输入与画面性能。',
            },
          },
        ],
      },
    ],
  }

  const script = document.createElement('script')
  script.id = STRUCTURED_DATA_ID
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(payload)
  document.head.appendChild(script)
}

export function initSeo(): void {
  applySeo()
  injectStructuredData()
}
